import { usePost } from "../../../api/useCall";
import type { Reparability } from "../../../api/Reparability";
import type { Project } from "../../../api/useProjects";
import { useMutateBody } from "../../../api/useQuery";
import type { FailureValueStatus } from "../Chart/PointMetadata";
import React from "react";

/**
 * Implement optimistic mutation of failure status.
 * @example
 * ```ts
 * const mutateFailureStatus = useFailureStatusMutation(project);
 *
 * const analysisFailureIds: { analysisId: string; failureId: string }[] = getFailureAnalysisId();
 * await mutateFailureStatus(analysisFailureIds, {status, reparability});
 * ```
 * @param project current project use in diagnostic view
 * @returns utility mutate function on failure status.
 */
export function useFailureValuesMutation(project: Project) {
  const updateFailuresStatus = usePost("/projects/:projectId(uuid)/failure-metadatas/status");
  const updateFailuresReparability = usePost(
    "/projects/:projectId(uuid)/failure-metadatas/reparability",
  );
  const mutateFailureValues = useMutateBody("/projects/:projectId(uuid)/failure-values", {
    routeParams: {
      projectId: project.id,
    },
  });

  return React.useCallback(
    async (
      failures: {
        date: number;
        failureName: string;
      }[],
      updateProperties: {
        status?: FailureValueStatus;
        reparability?: Reparability | null;
      },
    ) => {
      const failuresCopy = JSON.parse(JSON.stringify(failures)) as typeof failures;
      const rollback = mutateFailureValues(oldFailureValues =>
        oldFailureValues.map(oldFailureValue => {
          const match = failuresCopy.length
            ? failuresCopy.find(
                failure =>
                  failure.date === oldFailureValue.date &&
                  failure.failureName === oldFailureValue.failureName,
              )
            : undefined;

          if (match) {
            failuresCopy.splice(failuresCopy.indexOf(match), 1); // speed up next searches

            return {
              ...oldFailureValue,
              ...updateProperties,
            };
          }
          return oldFailureValue;
        }),
      );

      try {
        if (updateProperties.status !== undefined) {
          await updateFailuresStatus({
            routeParams: { projectId: project.id },
            body: {
              status: updateProperties.status === "TO_BE_ANALYSED" ? null : updateProperties.status,
              ids: failures.map(failure => ({
                failureName: failure.failureName,
                date: new Date(failure.date),
              })),
            },
          });
        }
        if (updateProperties.reparability !== undefined) {
          await updateFailuresReparability({
            routeParams: { projectId: project.id },
            body: {
              reparability: updateProperties.reparability,
              ids: failures.map(failure => ({
                failureName: failure.failureName,
                date: new Date(failure.date),
              })),
            },
          });
        }
      } catch (err) {
        rollback();
        throw err;
      }
    },
    [project, updateFailuresReparability, updateFailuresStatus, mutateFailureValues],
  );
}

export type FailureValuesMutation = ReturnType<typeof useFailureValuesMutation>;
